import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useResetPassword } from '@/hooks/use-api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import LoadingSpinner from '@/components/LoadingSpinner';
import logo from '/onextel-logo-white.png';

// Validation schema
const resetPasswordSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const [token, setToken] = useState<string>('');
  const resetPasswordMutation = useResetPassword();

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      // TODO: Validate token with backend
      setIsValidToken(true);
    } else {
      setIsValidToken(false);
    }
  }, [searchParams]);

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema as any),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (values: ResetPasswordFormData) => {
    if (!isValidToken) {
      toast.error('Invalid or expired reset token');
      return;
    }

    try {
      await resetPasswordMutation.mutateAsync({
        token,
        password: values.password,
        confirmPassword: values.confirmPassword,
      });
    } catch (error: any) {
      console.error('Reset password error:', error);
      // Handle validation errors from server
      if (error.response?.data?.errors) {
        error.response.data.errors.forEach((err: any) => {
          form.setError(err.field as keyof ResetPasswordFormData, {
            message: err.message,
          });
        });
      } else {
        // Let the hook handle the error - don't show duplicate toast
        console.log(
          'Letting hook handle error:',
          error.response?.data?.message
        );
      }
    }
  };

  if (isValidToken === null) {
    return (
      <main className="login-bg-container">
        <section className="dark:bg-main-dark-bg relative">
          <div className="flex flex-col items-center justify-center h-screen px-6 mx-auto">
            <LoadingSpinner size="lg" />
          </div>
        </section>
      </main>
    );
  }

  if (isValidToken === false) {
    return (
      <main className="login-bg-container">
        <section className="dark:bg-main-dark-bg relative">
          <div className="flex flex-col items-center justify-center h-screen px-6 mx-auto">
            <div className="w-full md:mt-0 max-w-screen-lg">
              <Card className="glassy-card flex flex-row w-full">
                <div className="w-full md:w-1/2 hidden md:flex flex-col justify-center items-center p-12 bg-gradient-to-br from-red-500 to-red-600 relative overflow-hidden">
                  {/* Decorative circles */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-red-400 rounded-full opacity-20 -mr-16 -mt-16"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-red-400 rounded-full opacity-20 -ml-12 -mb-12"></div>

                  <div className="flex flex-col justify-center items-center text-center z-10 max-w-sm">
                    <img className="w-32 mb-8" src={logo} alt="onextel-logo" />
                    <h1 className="text-white font-bold text-3xl mb-4">
                      Secure your account.
                    </h1>
                    <p className="text-white/90 text-sm mb-8 leading-relaxed">
                      This password reset link is invalid or has expired. Please
                      request a new one to continue securely.
                    </p>

                    <div className="space-y-4 w-full">
                      <div className="flex items-center gap-3 text-white">
                        <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <span className="text-sm">Link expired</span>
                      </div>
                      <div className="flex items-center gap-3 text-white">
                        <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9.5H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <span className="text-sm">Request new link</span>
                      </div>
                      <div className="flex items-center gap-3 text-white">
                        <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                          </svg>
                        </div>
                        <span className="text-sm">Check your email</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="w-full md:w-1/2 md:border-l-4 border-background pl-4 pr-4">
                  <CardHeader className="flex flex-col justify-center items-center pb-2">
                    <CardTitle className="text-2xl font-bold text-gray-900 text-center mb-2">
                      Invalid Reset Link
                    </CardTitle>
                    <p className="text-sm text-gray-600 text-center">
                      Please request a new password reset link
                    </p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-center text-sm text-gray-600 mb-4">
                      Please request a new password reset link.
                    </p>
                    <Button
                      onClick={() => navigate('/forgot-password')}
                      variant="outline"
                      className="w-full border-red-500 text-red-600 hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors"
                    >
                      Request New Link
                    </Button>
                  </CardContent>
                </div>
              </Card>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="login-bg-container">
      <section className="dark:bg-main-dark-bg relative">
        <div className="flex flex-col items-center justify-center h-screen px-6 mx-auto">
          <div className="w-full md:mt-0 max-w-screen-lg">
            <Card className="glassy-card flex flex-row w-full">
              <div className="w-full md:w-1/2 hidden md:flex flex-col justify-center items-center p-12 bg-gradient-to-br from-red-500 to-red-600 relative overflow-hidden">
                {/* Decorative circles */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-red-400 rounded-full opacity-20 -mr-16 -mt-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-red-400 rounded-full opacity-20 -ml-12 -mb-12"></div>

                <div className="flex flex-col justify-center items-center text-center z-10 max-w-sm">
                  <img className="w-32 mb-8" src={logo} alt="onextel-logo" />
                  <h1 className="text-white font-bold text-3xl mb-4">
                    Create a strong password.
                  </h1>
                  <p className="text-white/90 text-sm mb-8 leading-relaxed">
                    Enter your new password below to reset your account access
                    securely and continue your SMS campaigns.
                  </p>

                  <div className="space-y-4 w-full">
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <span className="text-sm">Enhanced security</span>
                    </div>
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <span className="text-sm">Verified protection</span>
                    </div>
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                        </svg>
                      </div>
                      <span className="text-sm">Account restored</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full md:w-1/2 md:border-l-4 border-background pl-4 pr-4">
                <CardHeader className="flex flex-col justify-center items-center pb-2">
                  <CardTitle className="text-2xl font-bold text-gray-900 text-center mb-2">
                    Reset Password
                  </CardTitle>
                  <p className="text-sm text-gray-600 text-center">
                    Enter your new password to secure your account
                  </p>
                </CardHeader>
                <CardContent>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-black">
                              New Password
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter new password"
                                type="password"
                                {...field}
                                disabled={resetPasswordMutation.isPending}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="confirmPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-black">
                              Confirm Password
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Confirm new password"
                                type="password"
                                {...field}
                                disabled={resetPasswordMutation.isPending}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button
                        className="w-full mt-6 bg-red-500 hover:bg-red-600 text-white font-medium"
                        type="submit"
                        disabled={resetPasswordMutation.isPending}
                      >
                        {resetPasswordMutation.isPending ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          'Reset Password'
                        )}
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ResetPasswordPage;
